<template>
  <b-row v-if="loading">
    <b-col
      sm="12"
      class="text-center"
    >
      <b-skeleton
        class="d-inline-block"
        animation="wave"
        width="55%"
        height="500px"
      />
    </b-col>
  </b-row>
  <section v-else>
    <scroll-container
      class="pt-2 mt-n30px"
      height-offset="140px"
    >
      <b-container class="container-small">
        <b-card
          class="mb-3"
        >
          <b-form-group :label="$tk('ClientPortal.Account.ChoosePaymentMethod')">
            <b-card
              v-if="configuration.paymentConfiguration.canPayWithCreditCard"
              class="border mb-50 cursor-pointer"
              body-class="py-1"
              :class="{'text-primary border-primary' : paymentMethod == 1}"
              @click="setPaymentMethod(1)"
            >
              <GjIcon
                class="mr-1"
                name="Payment"
                size="40px"
              />
              <span class="d-inline-block font-weight-bold mt-25">
                {{ $tk('ClientPortal.Account.PayWithCreditCard') }}
              </span>
            </b-card>
            <b-card
              v-if="configuration.paymentConfiguration.canPayWithBankTransfer"
              class="border mb-50 cursor-pointer"
              body-class="py-1"
              :class="{'text-primary border-primary' : paymentMethod == 2}"
              @click="setPaymentMethod(2)"
            >
              <GjIcon
                class="mr-1"
                name="Bank"
                size="40px"
              />
              <span class="d-inline-block font-weight-bold mt-25">
                {{ $tk('ClientPortal.Account.PayWithBankTransfer') }}
              </span>
            </b-card>
            <b-card
              v-if="configuration.paymentConfiguration.canPayWithRbko"
              class="border cursor-pointer"
              body-class="py-1"
              :class="{'text-primary border-primary' : paymentMethod == 3}"
              @click="setPaymentMethod(3)"
            >
              <GjIcon
                class="mr-1"
                name="PaymentAlt"
                size="40px"
              />
              <span class="d-inline-block font-weight-bold mt-25">
                {{ $tk('ClientPortal.Account.PayWithRbko') }}
              </span>
            </b-card>
          </b-form-group>
          <b-form-group
            v-if="paymentMethod != 0"
            class="mb-2"
            :label="$tk('ClientPortal.Deposit.Amount') + ' *'"
            :invalid-feedback="errorMessages.Amount"
            :state="errorMessages.Amount == null ? null : false"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                v-model.number="depositModel.amount"
                :state="errorMessages.Amount == null ? null : false"
                type="number"
                :placeholder="$currencyPlaceholder"
                autocomplete="off"
              />
              <b-input-group-append
                is-text
              >
                {{ $currencySymbol }}
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group
            v-if="paymentMethod == 1"
            :label="$tk('ClientPortal.Account.CardNumber')"
          >
            <stripe-form
              ref="stripeForm"
              :stripe-public-key="configuration.paymentConfiguration.stripePublicKey"
              :deposit-model="depositModel"
            />
          </b-form-group>
          <div
            v-else-if="paymentMethod == 2"
            class="mb-2"
          >
            <template v-if="configuration.paymentConfiguration.showBankTransferInfo">
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.CompanyName') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.companyName }}</span>
                </b-col>
              </b-form-row>
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.CompanyAddress') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.companyAddress }}</span>
                </b-col>
              </b-form-row>
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.AccountNumber') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.accountNumber }}</span>
                </b-col>
              </b-form-row>
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.IBAN') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.iban }}</span>
                </b-col>
              </b-form-row>
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.Swift') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.swift }}</span>
                </b-col>
              </b-form-row>
              <b-form-row class="mb-1">
                <b-col sm="4">
                  {{ $tk('ClientPortal.Deposit.BankTransfer.BankName') }}
                </b-col>
                <b-col sm="8">
                  <span class="text-dark font-weight-bold">{{ configuration.paymentConfiguration.bankName }}</span>
                </b-col>
              </b-form-row>
            </template>
          </div>
          <b-form-group
            v-if="paymentMethod != 0"
            class="mb-0"
            :label="$tk('ClientPortal.Deposit.Message')"
            :invalid-feedback="errorMessages['Message']"
          >
            <b-form-textarea
              v-model="depositModel.message"
              :placeholder="$tk('ClientPortal.Deposit.MessagePlaceholder')"
              :state="errorMessages['Message'] == null ? null : false"
              maxlength="480"
            />
          </b-form-group>
        </b-card>
      </b-container>
    </scroll-container>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          :to="`/${$route.params.accountId}/account/balance#transactions`"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-if="paymentMethod != 1"
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click.prevent="saveDeposit(depositModel)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
        <b-button
          v-else
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving"
          @click.prevent="paymentSubmit"
        >
          {{ $tk('ClientPortal.Account.SubmitPayment') }}
          <div
            v-if="saving"
            class="spinner-border spinner-border-sm ml-1"
            role="status"
          >
            <span class="sr-only">{{ $tk('ClientPortal.Account.Loading') }}...</span>
          </div>
        </b-button>
      </div>
    </div>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import ScrollContainer from '@/components/ScrollContainer.vue'
import AccountsService from '@/api/client/accounts.service'
import notification from '@/mixins/toastification'
import StripeForm from '@/components/StripeForm.vue'
import { mapGetters } from 'vuex'
import store from '@/store'

export default {
  components: {
    GjIcon,
    ScrollContainer,
    StripeForm,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: {},
      loading: true,
      paymentMethod: 1,
      depositModel: {},
      saving: false,
      errorMessages: {},
    }
  },
  computed: {
    ...mapGetters(['configuration']),
    isFormComplete() {
      return this.depositModel.amount
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const response = await AccountsService.getBalance('', '', this.$route.params.accountId)
        this.wrapper = response
      } catch (error) {
        this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
      } finally {
        this.loading = false
        this.paymentMethod = this.configuration.paymentConfiguration.defaultPaymentMethod
      }
    },
    setPaymentMethod(paymentMethod) {
      this.paymentMethod = paymentMethod
      this.errorMessages = {}
    },
    async saveDeposit(model) {
      this.saving = true
      const { accountId } = this.$route.params
      try {
        const response = await AccountsService.deposit(model, accountId)
        if (response.saved === true) {
          store.commit('app/TOGGLE_NEW_CREATE_STATE', true)
          this.success(response.message)
          this.$router.push(`/${accountId}/account/balance#transactions`)
        } else {
          this.errorMessages = response.errors
          this.error(this.$tk('ClientPortal.Account.Error.FailedToAddTransaction'))
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.saving = false
      }
    },
    async paymentSubmit() {
      this.saving = true
      try {
        await this.$refs.stripeForm.paymentSubmit()
        store.commit('app/TOGGLE_NEW_CREATE_STATE', true)
      } catch (error) {
        // console.log(error.message)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
